import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { TacoAssetType } from "@amzn/afxtaco-types";
import { useProjects } from "./useProjects";
import { reducerType } from "../../redux/reducers";

interface UseProjectAssetOptions {
  projectId: string | undefined;
  assetType: TacoAssetType;
  /** Skip fetching (e.g. for new projects that don't have assets yet) */
  skip?: boolean;
}

interface UseProjectAssetResult {
  /** The blob URL for the fetched asset, or undefined if not yet loaded */
  url: string | undefined;
  /** The raw text content for text-based assets (markdown), or undefined for binary assets */
  content: string | undefined;
  /** Whether the asset is currently being fetched */
  isLoading: boolean;
  /** Whether the fetch failed with a 404 (asset doesn't exist yet) */
  isNotFound: boolean;
  /** Manually trigger a retry (e.g. when user navigates to the tab) */
  retry: () => void;
}

/**
 * Hook that manages fetching, caching, retrying, and cleanup for a single
 * project asset type. Replaces the pattern of multiple useEffects per asset
 * in ArchitectureComposerPage.
 *
 * @example
 * ```tsx
 * const diagram = useProjectAsset({
 *   projectId,
 *   assetType: TacoAssetType.ARCHITECTURE_DIAGRAM,
 *   skip: location.state?.isNewProject,
 * });
 *
 * if (diagram.isLoading) return <Spinner />;
 * if (diagram.url) return <img src={diagram.url} />;
 * ```
 */
export const useProjectAsset = ({
  projectId,
  assetType,
  skip = false,
}: UseProjectAssetOptions): UseProjectAssetResult => {
  const { getProjectAsset, fetchProjectAsset, clearProjectAssetUrl } =
    useProjects();

  const url = projectId ? getProjectAsset(projectId, assetType) : undefined;

  // Watch both completion counters from Redux, bumped by the
  // handleExecutionCompleted thunk: staleCounter on new-content (asset
  // generation) turns, refetchCounter on every turn (to pick up files that
  // appeared mid-turn, e.g. a legacy migration on a chat-only turn). Either
  // bump invalidates our cached/attempted state so the asset re-fetches —
  // replacing the pattern of parent components calling retry() after completion.
  const staleCounter = useSelector((state: reducerType) =>
    projectId ? (state.Projects.assetsStaleCounter[projectId] ?? 0) : 0,
  );
  const refetchCounter = useSelector((state: reducerType) =>
    projectId ? (state.Projects.assetsRefetchCounter[projectId] ?? 0) : 0,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [content, setContent] = useState<string | undefined>(undefined);

  // Track which project+version combos we've already attempted so we don't
  // re-fetch on every render.
  const attemptedRef = useRef<Set<string>>(new Set());

  // When retry() is called explicitly, we need to bypass the `skip` guard
  // because the caller knows assets are now available (e.g. after WebSocket completion).
  const skipOverrideRef = useRef(false);

  // Bumped by retry() to force the auto-fetch effect to re-evaluate
  const [retryCounter, setRetryCounter] = useState(0);

  const doFetch = useCallback(async () => {
    if (!projectId) return;
    if (skip && !skipOverrideRef.current) return;

    if (attemptedRef.current.has(projectId)) return;

    // Only consume the skip override once we're actually going to attempt a
    // fetch. This ensures the override survives across re-renders until the
    // fetch is made.
    skipOverrideRef.current = false;

    attemptedRef.current.add(projectId);
    setIsLoading(true);
    setIsNotFound(false);
    setContent(undefined);

    try {
      // Read the current (live) file — no versionNumber.
      const result = await fetchProjectAsset({
        projectId,
        assetType,
      });

      // For text-based assets, use the text already extracted from the
      // in-memory Blob by fetchProjectAsset — no extra request needed.
      if (result?.text != null) {
        setContent(result.text);
      }
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = error as Record<string, any>;
      const statusCode =
        err?.$metadata?.httpStatusCode ||
        err?._response?.statusCode ||
        err?.response?.status ||
        err?.status;

      if (statusCode === 404) {
        // 404 is expected for partially-generated projects where some asset types don't exist yet
        setIsNotFound(true);
      } else {
        console.error(`Failed to load ${assetType} asset:`, error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId, skip, assetType, fetchProjectAsset]);

  // Auto-fetch on mount / when project data becomes available / after retry or invalidation
  useEffect(() => {
    if (!url && !isLoading) {
      doFetch();
    }
  }, [url, isLoading, doFetch, retryCounter, staleCounter, refetchCounter]);

  // Hydrate local `content` from the cached blob URL when it exists in Redux
  // but content hasn't been populated (happens on remount: another hook instance
  // restored the blob URL, but our local state was destroyed). Reads text
  // directly from the in-memory blob — no S3 round-trip. If the blob is
  // unreadable (revoked), falls back to a full refetch.
  useEffect(() => {
    if (
      url &&
      content === undefined &&
      !isLoading &&
      !isNotFound &&
      typeof globalThis.fetch === "function"
    ) {
      let cancelled = false;
      globalThis
        .fetch(url)
        .then((res) => res.text())
        .then((text) => {
          if (!cancelled && text) setContent(text);
        })
        .catch(() => {
          // Blob URL revoked or unreadable — full refetch.
          if (!cancelled) {
            attemptedRef.current.clear();
            doFetch();
          }
        });
      return () => {
        cancelled = true;
      };
    }
  }, [url, content, isLoading, isNotFound, doFetch]);

  // When either completion counter bumps (assets invalidated or a re-fetch
  // forced by the completion thunk), clear local tracking state so the
  // auto-fetch effect can re-fetch. Done synchronously during render (not in an
  // effect) so skipOverrideRef and attemptedRef are already updated when the
  // auto-fetch effect runs.
  const prevStaleCounterRef = useRef(staleCounter);
  const prevRefetchCounterRef = useRef(refetchCounter);
  if (
    staleCounter > prevStaleCounterRef.current ||
    refetchCounter > prevRefetchCounterRef.current
  ) {
    skipOverrideRef.current = true;
    attemptedRef.current.clear();
    prevStaleCounterRef.current = staleCounter;
    prevRefetchCounterRef.current = refetchCounter;
  }

  // URL cleared externally (e.g. clearProjectAsset) → forget attempt history
  // so doFetch can re-fetch.
  const prevUrlRef = useRef(url);
  if (prevUrlRef.current !== undefined && url === undefined) {
    attemptedRef.current.clear();
  }
  prevUrlRef.current = url;

  // Allow consumers to retry after a 404 or after generation completes.
  // Sets flags and triggers a state update so the auto-fetch effect re-fires.
  const retry = useCallback(() => {
    if (!projectId || isLoading) return;

    // Set the override so doFetch bypasses the skip guard on next run
    skipOverrideRef.current = true;
    attemptedRef.current.clear();
    setIsNotFound(false);
    setContent(undefined);

    // Clear cached blob URL
    clearProjectAssetUrl(projectId, assetType);

    // Bump counter to force a re-render so the auto-fetch effect re-evaluates
    setRetryCounter((c) => c + 1);
  }, [projectId, isLoading, clearProjectAssetUrl, assetType]);

  // Cleanup blob URL on unmount or projectId change. Also wipes the
  // attempted-fetch set: without this, a future remount or return-to-
  // project would short-circuit doFetch's `attemptedRef.has(fetchKey)`
  // guard against stale entries (the URL was cleared but the ref still
  // says "we tried this version"), leaving the UI stuck on "asset not
  // available" until a stale-counter bump or full reload.
  useEffect(() => {
    const attempted = attemptedRef;
    return () => {
      if (projectId) {
        clearProjectAssetUrl(projectId, assetType);
        attempted.current.clear();
      }
    };
    // Only cleanup on unmount or when project ID changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return { url, content, isLoading, isNotFound, retry };
};
